// Round 20 — Sunscreen & Insect Repellent Authorization.
//
// Single source of truth for the two branded products, the parent-facing
// wording, and the small read/fold helpers — mirroring otcMedications.ts
// (Round 18/19) so the Step 3 UI, the PHI export, the PDF generator, the
// tracker writer, AND round20.test.ts all render from the same strings.
//
// DELIBERATELY SEPARATE FROM OTC. This is its own section with its own
// acknowledgement and its own signature, so a parent can authorize one product
// without the other and the two consent types never blur together. Per the
// program's medical supervisor these are explicitly NOT medications: no
// dose/frequency/administration (MAR-style) logging, no Physician Medication
// Authorization, and they are never added to the medications table. Only the
// authorization itself needs to be on file.
//
// Brand names are named verbatim in the parent-facing consent (required by the
// state). TOPICALS_VERSION is snapshotted onto each record — the same purpose as
// OTC_LIST_VERSION — so that if the brands change in a future year, existing
// records still show what was actually consented to at the time.

import type {
  OtcPermission,
  TopicalItemPermissions,
  TopicalsAuthorization,
} from "./types";

/** Bump when the products or their wording change materially (stored per record). */
export const TOPICALS_VERSION = "2027.1";

/** Token replaced with the running program's name at render time. */
export const TOPICALS_PROGRAM_TOKEN = "{PROGRAM}";

// Exact strings — the state requires the actual brand to be named in the
// parent-facing consent. Do not abbreviate these in the UI, PDF, or export.
export const SUNSCREEN_BRAND = "Equate Sport Broad Spectrum Sunscreen, SPF 50";
export const INSECT_REPELLENT_BRAND =
  "Repel Sportsmen Max Formula Insect and Mosquito Repellent, Bug Spray with 40% DEET";

/** The two permissions captured per product. */
export type TopicalPermissionKey = "give" | "assist";

/** The two products, keyed by their field name on TopicalsAuthorization. */
export type TopicalItemKey = "sunscreen" | "insectRepellent";

export interface TopicalItem {
  key: TopicalItemKey;
  /** Short section heading, e.g. "Sunscreen". */
  shortLabel: string;
  /** The verbatim branded product name. */
  brand: string;
  /** The two permission prompts, in UI / export / PDF order. */
  permissions: Array<{ key: TopicalPermissionKey; label: string; exportLabel: string }>;
}

export const TOPICAL_ITEMS: TopicalItem[] = [
  {
    key: "sunscreen",
    shortLabel: "Sunscreen",
    brand: SUNSCREEN_BRAND,
    permissions: [
      {
        key: "give",
        label: "I authorize staff to give/apply this sunscreen to my child as needed.",
        exportLabel: "Sunscreen — Give",
      },
      {
        key: "assist",
        label:
          "I authorize staff to assist my child in applying this sunscreen (e.g., hard-to-reach areas).",
        exportLabel: "Sunscreen — Assist",
      },
    ],
  },
  {
    key: "insectRepellent",
    shortLabel: "Insect Repellent",
    brand: INSECT_REPELLENT_BRAND,
    permissions: [
      {
        key: "give",
        label: "I authorize staff to give/apply this insect repellent to my child as needed.",
        exportLabel: "Insect Repellent — Give",
      },
      {
        key: "assist",
        label:
          "I authorize staff to assist my child in applying this insect repellent (e.g., hard-to-reach areas).",
        exportLabel: "Insect Repellent — Assist",
      },
    ],
  },
];

export const TOPICALS_INTRO_TEXT = `Please indicate whether you authorize the ${TOPICALS_PROGRAM_TOKEN} staff to give/apply, or to assist your child in applying, the sunscreen and insect repellent provided at the session. These are not medications and are not administered on a schedule; they are used as needed during outdoor activities.`;

export const TOPICALS_BLANK_DEFAULT_NOTE =
  "Any item left blank will be treated as not authorized (No).";

export const TOPICALS_ACKNOWLEDGEMENT_TEXT = `I authorize the ${TOPICALS_PROGRAM_TOKEN} medical staff to give/apply and/or assist my child with ${SUNSCREEN_BRAND} and ${INSECT_REPELLENT_BRAND}, according to the permissions I have selected above.`;

/** Substitutes the program name for the {PROGRAM} token in a parent-facing string. */
export function withTopicalsProgramName(text: string, programName: string): string {
  return text.split(TOPICALS_PROGRAM_TOKEN).join(programName);
}

/** Intro paragraph with the program name substituted in. */
export function topicalsIntroText(programName: string): string {
  return withTopicalsProgramName(TOPICALS_INTRO_TEXT, programName);
}

/** Acknowledgement paragraph with the program name substituted in. */
export function topicalsAcknowledgementText(programName: string): string {
  return withTopicalsProgramName(TOPICALS_ACKNOWLEDGEMENT_TEXT, programName);
}

/** A blank pair of permissions for one product (both unanswered). */
export function emptyTopicalItemPermissions(): TopicalItemPermissions {
  return { give: null, assist: null };
}

/** A blank topicals block (used for defaults and back-fill of pre-Round-20 drafts). */
export function emptyTopicalsAuthorization(): TopicalsAuthorization {
  return {
    listVersion: TOPICALS_VERSION,
    sunscreen: emptyTopicalItemPermissions(),
    insectRepellent: emptyTopicalItemPermissions(),
    acknowledged: false,
    acknowledgementSignature: null,
    acknowledgedAtUtc: null,
  };
}

/**
 * Read-time coercion: a permission the parent never selected reads as "no" —
 * the safest default, and exactly the rule otcPermissionFor applies to the OTC
 * per-item selections. Used by the export, the PDF, and the tracker fold so
 * they all agree.
 */
export function topicalPermissionFor(
  topicals: TopicalsAuthorization | undefined | null,
  item: TopicalItemKey,
  permission: TopicalPermissionKey
): OtcPermission {
  const value = topicals?.[item]?.[permission];
  if (value === "yes" || value === "call") return value;
  return "no";
}

/** Human label for a permission value (shared wording with the OTC table). */
export function topicalPermissionLabel(p: OtcPermission): "Yes" | "No" | "Call First" {
  return p === "yes" ? "Yes" : p === "call" ? "Call First" : "No";
}

/**
 * Compact tracker fold of the parent's EXPLICIT selections, mirroring
 * otcTrackerSummary's rules exactly — e.g.
 *   "Sunscreen: Give Yes/Assist No | Repellent: Give Call First/Assist No"
 * A permission left BLANK is the default and is omitted (it reads as No
 * everywhere else); a product with nothing but blanks drops out entirely.
 * Returns "None authorized" when nothing is authorized (no Yes and no Call
 * First anywhere), matching the OTC column's "None authorized".
 */
export function topicalsTrackerSummary(
  topicals: TopicalsAuthorization | undefined | null
): string {
  const parts: string[] = [];
  let anyAuthorized = false;
  for (const item of TOPICAL_ITEMS) {
    const bits: string[] = [];
    for (const p of item.permissions) {
      const raw = topicals?.[item.key]?.[p.key];
      // Blank = default = omitted from the fold (same as an unanswered OTC item).
      if (raw !== "yes" && raw !== "no" && raw !== "call") continue;
      if (raw === "yes" || raw === "call") anyAuthorized = true;
      bits.push(`${title(p.key)} ${topicalPermissionLabel(raw)}`);
    }
    if (bits.length) parts.push(`${shortTrackerName(item.key)}: ${bits.join("/")}`);
  }
  if (!anyAuthorized) return "None authorized";
  return parts.join(" | ");
}

function title(key: TopicalPermissionKey): string {
  return key === "give" ? "Give" : "Assist";
}

/** Tracker-cell short names, kept compact ("Repellent", not the full brand). */
function shortTrackerName(key: TopicalItemKey): string {
  return key === "sunscreen" ? "Sunscreen" : "Repellent";
}
